/**
 * Página pública del portal de resultados.
 * Ruta: /portal/resultados?t=TOKEN
 * Optimizada para móviles y tablets. PDF solo cuando todos los resultados están ingresados.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PictureAsPdf, InfoOutlined } from '@mui/icons-material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API_URL, BASE_URL } from '../config';
import { ACCENT_COLOR_VERDE_OFICIAL } from '../utils/pdfResultadosLaboratorio';
import { crearPdfResultadosLaboratorio, guardarPdfResultado } from '../utils/pdfResultadosLaboratorio';

interface ResultadoItem {
  componente_id?: number | null;
  componente_nombre: string;
  valor: string | null;
  unidad: string | null;
  valor_referencia: string | null;
  observaciones: string | null;
  clase?: string | null;
  punto_tiempo_min?: number | null;
  valor_referencia_min?: number | null;
  valor_referencia_max?: number | null;
}

interface ExamenItem {
  examen_id: number;
  codigo: string;
  nombre: string;
  categoria_id?: number | null;
  categoria_nombre: string;
  resultados: ResultadoItem[];
}

interface FormatoResultados {
  id?: number;
  nombre?: string;
  design_json?: string | null;
  logo_url?: string | null;
  banner_encabezado_url?: string | null;
  banner_pie_url?: string | null;
  configuracion_json?: string | null;
}

interface PortalData {
  paciente: {
    nombre: string;
    documento_identidad?: string;
    fecha_nacimiento?: string;
    sexo?: string;
    direccion?: string;
    telefono?: string;
  };
  orden: {
    id: number;
    numero_orden: string;
    fecha_orden: string;
    medico_nombre?: string;
    tipo?: 'facturada' | 'tecnica';
    factura_id?: number | null;
    numero_factura?: string;
    factura_saldo_pendiente?: number;
  };
  todos_resultados_ingresados?: boolean;
  resultados_bloqueados_por_pago?: boolean;
  examenes: ExamenItem[];
  laboratorio?: {
    nombre: string;
    logo_url: string | null;
    portal_encabezado_texto: string | null;
    direccion?: string | null;
    telefono?: string | null;
    ruc?: string | null;
    whatsapp_telefono?: string | null;
    whatsapp_texto?: string | null;
    /** Paleta de colores (igual que en Configuración) para que el PDF del portal use los mismos colores que el módulo de órdenes */
    paleta?: unknown;
  };
  formato_resultados?: FormatoResultados | null;
  /** Nonce de un solo uso para registrar descarga PDF (ack-pdf) */
  ack_pdf_nonce?: string | null;
}

const PortalResultados: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || searchParams.get('token') || '';
  const [data, setData] = useState<PortalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!token.trim()) {
      setError('No se proporcionó enlace de resultados.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get<PortalData>(`${API_URL}/portal/resultados`, { params: { t: token } })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || err.message || 'Error al cargar resultados';
          setError(msg);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          px: 2,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 3, px: 2 }}>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Si el enlace ha expirado o no es válido, solicite uno nuevo en el laboratorio.
        </Typography>
      </Container>
    );
  }

  if (!data) return null;

  const { paciente, orden, examenes, laboratorio } = data;
  const todosIngresados = data.todos_resultados_ingresados !== false;
  const esOrdenTecnica =
    orden.tipo === 'tecnica' ||
    orden.factura_id == null ||
    !orden.numero_factura;
  const pendientePago =
    data.resultados_bloqueados_por_pago === true ||
    (!esOrdenTecnica && Number(orden.factura_saldo_pendiente ?? 0) > 0);
  const saldoPendiente = Number(orden.factura_saldo_pendiente ?? 0);
  const formatMonto = (n: number) =>
    `C$ ${n.toLocaleString('es-NI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const encabezadoTexto = (laboratorio?.portal_encabezado_texto || '').trim() || 'Resultados de laboratorio';
  const rawLogoUrl = laboratorio?.logo_url || null;
  const logoUrl = rawLogoUrl && !rawLogoUrl.startsWith('http') && !rawLogoUrl.startsWith('data:')
    ? `${BASE_URL}${rawLogoUrl.startsWith('/') ? '' : '/'}${rawLogoUrl}`
    : rawLogoUrl;
  const nombreLab = (laboratorio?.nombre || '').trim();
  const fechaOrden = orden.fecha_orden
    ? new Date(orden.fecha_orden).toLocaleDateString('es-DO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : orden.fecha_orden;

  const handleDescargarPdf = async () => {
    if (!data || !todosIngresados || pendientePago) return;
    setGenerandoPdf(true);
    setPdfError(null);
    try {
      const ordenForPdf = {
        id: data.orden.id,
        paciente_nombre: data.paciente.nombre,
        paciente_edad: null as string | null,
        paciente_direccion: data.paciente.direccion ?? undefined,
        paciente_telefono: data.paciente.telefono ?? undefined,
        sexo: data.paciente.sexo ?? undefined,
        fecha_nacimiento: data.paciente.fecha_nacimiento,
        numero_orden: data.orden.numero_orden,
        fecha_orden: data.orden.fecha_orden,
        medico_nombre: data.orden.medico_nombre,
        examenes: data.examenes,
      };
      const configForPdf = {
        nombre: data.laboratorio?.nombre || 'Laboratorio',
        logo_url: data.laboratorio?.logo_url ?? null,
        direccion: data.laboratorio?.direccion ?? '',
        telefono: data.laboratorio?.telefono ?? '',
        ruc: data.laboratorio?.ruc ?? '',
        whatsapp_telefono: data.laboratorio?.whatsapp_telefono ?? data.laboratorio?.telefono ?? '',
        whatsapp_texto: data.laboratorio?.whatsapp_texto ?? '',
        paleta: data.laboratorio?.paleta ?? null,
      };
      const pdf = await crearPdfResultadosLaboratorio({
        orden: { ...ordenForPdf, factura_saldo_pendiente: orden.factura_saldo_pendiente ?? 0 },
        config: configForPdf,
        formatoResultados: data.formato_resultados ?? null,
        portalToken: token,
      });
      const nombreArchivo = `resultados_${data.orden.numero_orden || data.orden.id}.pdf`;
      guardarPdfResultado(pdf, nombreArchivo);
      // Registrar descarga → ícono WhatsApp verde (requiere nonce del GET portal)
      if (token && data?.ack_pdf_nonce) {
        void axios
          .post(`${API_URL}/portal/resultados/ack-pdf`, {
            t: token,
            ack_nonce: data.ack_pdf_nonce,
          })
          .catch(() => {});
      }
    } catch (e) {
      console.error('Error al generar PDF:', e);
      setPdfError(e instanceof Error ? e.message : 'Error al generar PDF');
    } finally {
      setGenerandoPdf(false);
    }
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        pb: 4,
        px: isMobile ? 1.5 : 2,
      }}
    >
      <Container
        maxWidth="md"
        disableGutters={isMobile}
        sx={{
          py: isMobile ? 2 : 3,
          px: isMobile ? 1.5 : 2,
        }}
      >
        {/* Cabecera: logo + texto personalizable + botón PDF */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
            {logoUrl && (
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.75,
                  display: 'inline-flex',
                  alignItems: 'center',
                  boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt={nombreLab || 'Logo'}
                  sx={{
                    maxHeight: 48,
                    maxWidth: 160,
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            )}
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                fontWeight: 600,
                color: isDark ? 'primary.light' : 'primary.dark',
              }}
            >
              {encabezadoTexto}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={generandoPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
            onClick={() => {
              setPdfError(null);
              void handleDescargarPdf();
            }}
            disabled={!todosIngresados || generandoPdf || pendientePago}
            fullWidth={isMobile}
            sx={{ minWidth: isMobile ? undefined : 160 }}
          >
            {generandoPdf ? 'Generando...' : pendientePago ? 'Pendiente de Pago' : 'Descargar PDF'}
          </Button>
        </Box>

        {pdfError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>
            {pdfError}
          </Alert>
        )}

        {/* Aviso cuando faltan resultados (solo si no hay bloqueo por pago) */}
        {!todosIngresados && !pendientePago && (
          <Alert
            severity="info"
            icon={<InfoOutlined />}
            sx={{ mb: 2 }}
          >
            Los resultados estarán disponibles en el portal cuando el laboratorio haya ingresado
            todos los valores. El PDF se habilitará al mismo tiempo.
          </Alert>
        )}

        {/* Aviso de cobro solo en órdenes facturadas */}
        {!esOrdenTecnica && pendientePago && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pendiente de pago
            {orden.numero_factura ? ` (factura ${orden.numero_factura})` : ''}
            : saldo pendiente <strong>{formatMonto(saldoPendiente)}</strong>.
            Debe cancelar el saldo para poder consultar, descargar o imprimir los resultados.
          </Alert>
        )}

        {/* Datos del paciente */}
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 1.5 : 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: isDark ? 'rgba(255,255,255,0.68)' : 'text.secondary' }}
          >
            Paciente
          </Typography>
          <Typography variant="body1" fontWeight="medium" color="text.primary">
            {paciente.nombre}
          </Typography>
          {paciente.documento_identidad && (
            <Typography
              variant="body2"
              sx={{ color: isDark ? 'rgba(255,255,255,0.68)' : 'text.secondary' }}
            >
              Documento: {paciente.documento_identidad}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{ mt: 1, color: isDark ? 'rgba(255,255,255,0.68)' : 'text.secondary' }}
          >
            Orden: {orden.numero_orden} · Fecha: {fechaOrden}
            {orden.medico_nombre && ` · Médico: ${orden.medico_nombre}`}
          </Typography>
        </Paper>

        {/* Tablas por examen: ocultas si hay saldo pendiente */}
        {!pendientePago &&
          examenes.map((examen) => (
          <Paper
            key={examen.examen_id}
            elevation={0}
            sx={{
              mb: 2,
              overflow: 'hidden',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                px: isMobile ? 1.5 : 2,
                py: 1.5,
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                {examen.nombre}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.68)' : 'text.secondary',
                }}
              >
                {examen.categoria_nombre}
                {examen.codigo ? ` · ${examen.codigo}` : ''}
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: isMobile ? 320 : 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : undefined }}>
                      Componente
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : undefined }}>
                      Resultado
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Unidad</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : undefined }}>
                      {isMobile ? 'Ref.' : 'Valor de referencia'}
                    </TableCell>
                    {examen.resultados.some((r) => r.observaciones) && !isMobile && (
                      <TableCell sx={{ fontWeight: 600 }}>Observaciones</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examen.resultados.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>
                        {r.componente_nombre}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, fontSize: isMobile ? '0.8rem' : undefined }}>
                        {r.valor ?? '—'}
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.unidad ?? '—'}</TableCell>
                      )}
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : undefined }}>
                        {r.valor_referencia ?? '—'}
                      </TableCell>
                      {examen.resultados.some((x) => x.observaciones) && !isMobile && (
                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.observaciones ?? '—'}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* En móvil, observaciones debajo de la fila si existen */}
            {isMobile && examen.resultados.some((r) => r.observaciones) && (
              <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
                {examen.resultados
                  .filter((r) => r.observaciones)
                  .map((r, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      <strong>{r.componente_nombre}:</strong> {r.observaciones}
                    </Typography>
                  ))}
              </Box>
            )}
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default PortalResultados;
